import { useState, useEffect } from 'react';
import api from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { PackagePlus, MessageSquare } from 'lucide-react';

export const InstallationExtras = ({ installation, onUpdated }) => {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelected((installation.extra_products || []).map(p => ({ ...p })));
    setRemarks(installation.extra_remarks || '');
    api.get('/installation-products').then(res => {
      const items = Array.isArray(res.data) ? res.data : (res.data?.products || []);
      setProducts(items);
    }).catch(() => toast.error('Kon producten niet laden'));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (p, checked) => {
    if (checked) setSelected(prev => [...prev, { id: p.id, name: p.name, brand: p.brand || '', quantity: 1 }]);
    else setSelected(prev => prev.filter(s => s.id !== p.id));
  };

  const setQty = (id, qty) => setSelected(prev => prev.map(s => s.id === id ? { ...s, quantity: Math.max(1, parseInt(qty) || 1) } : s));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/scheduled-installations/${installation.id}`, { extra_products: selected, extra_remarks: remarks });
      onUpdated({ extra_products: selected, extra_remarks: remarks });
      toast.success("Extra's opgeslagen");
      setOpen(false);
    } catch (e) {
      toast.error("Kon extra's niet opslaan");
    } finally {
      setSaving(false);
    }
  };

  const filtered = products.filter(p => `${p.brand || ''} ${p.name}`.toLowerCase().includes(search.toLowerCase()));
  const hasExtras = (installation.extra_products || []).length > 0 || installation.extra_remarks;

  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-1.5">
        {(installation.extra_products || []).map((p, i) => (
          <Badge key={i} variant="outline" className="text-[11px] bg-orange-50 text-orange-700 border-orange-300" data-testid={`extra-product-badge-${installation.id}-${i}`}>
            Extra: {p.quantity > 1 ? `${p.quantity}x ` : ''}{p.name}
          </Badge>
        ))}
        <button
          className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-primary underline-offset-2 hover:underline"
          onClick={() => setOpen(true)}
          data-testid={`extras-open-${installation.id}`}>
          <PackagePlus className="h-3 w-3" /> {hasExtras ? "Extra's bewerken" : "Extra's / opmerkingen toevoegen"}
        </button>
      </div>
      {installation.extra_remarks && (
        <p className="mt-1 text-xs text-muted-foreground flex items-start gap-1" data-testid={`extra-remarks-${installation.id}`}>
          <MessageSquare className="h-3 w-3 mt-0.5 flex-shrink-0" /> {installation.extra_remarks}
        </p>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="extras-dialog">
          <DialogHeader>
            <DialogTitle>Extra producten & opmerkingen</DialogTitle>
            <DialogDescription>Extra producten die nodig zijn om de installatie af te ronden</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-sm font-medium">Opmerkingen</label>
              <Textarea value={remarks} onChange={(e) => setRemarks(e.target.value)} rows={2}
                placeholder="bv. extra kabel nodig langs achtergevel..." data-testid="extras-remarks-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Producten</label>
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Zoek product..." className="mb-2" data-testid="extras-search-input" />
              <div className="border rounded-md max-h-[260px] overflow-y-auto divide-y">
                {filtered.map(p => {
                  const sel = selected.find(s => s.id === p.id);
                  return (
                    <div key={p.id} className="flex items-center gap-2 px-2 py-1.5">
                      <Checkbox checked={!!sel} onCheckedChange={(c) => toggle(p, c)} data-testid={`extras-product-${p.id}`} />
                      <span className="text-sm flex-1 truncate">{p.brand ? `${p.brand} — ` : ''}{p.name}</span>
                      {sel && (
                        <Input type="number" min={1} value={sel.quantity} onChange={(e) => setQty(p.id, e.target.value)}
                          className="w-16 h-7 text-sm" data-testid={`extras-qty-${p.id}`} />
                      )}
                    </div>
                  );
                })}
                {filtered.length === 0 && <p className="text-sm text-muted-foreground p-3">Geen producten gevonden</p>}
              </div>
              {selected.length > 0 && (
                <p className="text-xs text-muted-foreground mt-1">{selected.length} geselecteerd: {selected.map(s => s.name).join(', ')}</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuleren</Button>
            <Button onClick={save} disabled={saving} data-testid="extras-save-btn">{saving ? 'Opslaan...' : 'Opslaan'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
